/**
 * Widget L: Longevity Stress Test Summary
 * 
 * Displays a grouped bar chart or KPI tiles showing:
 * - At-risk count (clients who will outlive their savings)
 * - Funded count (clients funded through life expectancy)
 * - Average years short for at-risk clients
 */

import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { ClientType } from "../../../types";
import { calculateLongevityStress, LongevityStressData } from "../utils/widgetCalculations";
import { SEMANTIC_PASTELS, ACCENT_COLOR } from "./widgetColors";
import { ChartTooltip, SIMPLE_BAR_MARGINS, AXIS_STYLE, GRID_STYLE, CHART_ANIMATION } from "./chartUtils";
import XanoAPI from "../../../lib/XanoAPI";

interface LongevityStressWidgetProps {
  clients: ClientType[];
  isLoading?: boolean;
  userId?: number; // Optional user ID filter (singular int)
}

export function LongevityStressWidget({
  clients,
  isLoading = false,
  userId,
}: LongevityStressWidgetProps) {
  const api = XanoAPI.getInstance();

  // Fetch data from API endpoint
  const { data: apiData, isLoading: apiLoading } = useQuery({
    queryKey: ["longevity-stress", userId],
    queryFn: async () => {
      const result = await api.getLongevityStress(userId);
      console.log("🔍 LongevityStressWidget: API response:", result);
      console.log("🔍 LongevityStressWidget: API response type:", typeof result);
      console.log("🔍 LongevityStressWidget: Has atRiskCount?", typeof result?.atRiskCount === 'number');
      console.log("🔍 LongevityStressWidget: Has fundedCount?", typeof result?.fundedCount === 'number');
      if (!result) {
        console.log("⚠️ LongevityStressWidget: Endpoint returned null");
      }
      return result;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  const data = useMemo(() => {
    // Only use API data - no sample data fallback
    let result: {
      atRiskCount: number;
      fundedCount: number;
      avgYearsShort: number;
    };
    
    if (apiData && typeof apiData.atRiskCount === 'number' && typeof apiData.fundedCount === 'number') {
      // API data is available - use it
      result = {
        atRiskCount: apiData.atRiskCount,
        fundedCount: apiData.fundedCount,
        avgYearsShort: apiData.avgYearsShort ?? 0,
      };
    } else {
      // No data available - return empty structure
      result = {
        atRiskCount: 0,
        fundedCount: 0,
        avgYearsShort: 0,
      };
    }
    
    // Transform to chart data format
    const totalClients = result.atRiskCount + result.fundedCount;
    
    const chartData = [
      {
        category: "At Risk",
        count: result.atRiskCount,
        percentage: totalClients > 0 ? (result.atRiskCount / totalClients) * 100 : 0,
        fill: SEMANTIC_PASTELS.error, // Coral (pastel)
        key: "atRisk",
        avgYearsShort: result.avgYearsShort,
      },
      {
        category: "Funded",
        count: result.fundedCount,
        percentage: totalClients > 0 ? (result.fundedCount / totalClients) * 100 : 0,
        fill: SEMANTIC_PASTELS.success, // Mint green (pastel)
        key: "funded",
      },
    ].filter((item) => item.count > 0);

    return { chartData, totalClients, avgYearsShort: result.avgYearsShort };
  }, [apiData]);

  const isLoadingData = isLoading || apiLoading;

  if (isLoadingData) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.totalClients === 0 || data.chartData.length === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Longevity Stress Test</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground">
              No longevity stress test data available for the selected clients.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }


  const chartDataWithTotal = data.chartData.map(item => ({
    ...item,
    totalClients: data.totalClients,
  }));

  return (
    <Card>
      <CardContent>
        <div className="flex flex-col items-start space-y-1 pb-2 mb-4">
          <div className="flex flex-row items-center justify-between w-full">
            <CardTitle className="text-sm font-medium">Longevity Stress Test</CardTitle>
            {data.totalClients > 0 && (
              <span className="text-xs text-muted-foreground">({data.totalClients})</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Clients at risk of outliving their savings</p>
        </div>
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartDataWithTotal} margin={SIMPLE_BAR_MARGINS}>
              <CartesianGrid {...GRID_STYLE} vertical={false} />
              <XAxis 
                dataKey="category" 
                tick={AXIS_STYLE.tick}
                tickLine={false}
              />
              <YAxis 
                label={{ 
                  value: "Number of Clients", 
                  angle: -90, 
                  position: "insideLeft",
                  style: AXIS_STYLE.label
                }}
                allowDecimals={false}
                tick={AXIS_STYLE.tick}
                tickLine={false}
                width={25}
              />
              <Tooltip 
                content={<ChartTooltip 
                  formatter={(value, name) => {
                    const dataPoint = chartDataWithTotal.find(d => d.count === value);
                    const yearsInfo = dataPoint?.avgYearsShort !== undefined && dataPoint.avgYearsShort > 0
                      ? `, Avg ${dataPoint.avgYearsShort.toFixed(1)} yrs short`
                      : "";
                    return [
                      `${value} clients (${dataPoint?.percentage.toFixed(1)}%${yearsInfo})`,
                      dataPoint?.category || name
                    ];
                  }}
                />} 
                cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
              />
              <Bar 
                dataKey="count" 
                radius={[8, 8, 0, 0]}
                {...CHART_ANIMATION}
              >
                {chartDataWithTotal.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 pt-3 border-t">
          {data.chartData.map((item) => (
            <div key={item.key} className="space-y-1 text-center">
              <div className="text-2xl font-bold" style={{ color: item.fill }}>
                {item.count}
              </div>
              <div className="text-xs font-medium text-foreground">{item.category}</div>
              <div className="text-xs text-muted-foreground">
                {item.percentage.toFixed(1)}%
              </div>
              {item.avgYearsShort !== undefined && item.avgYearsShort > 0 && (
                <div className="text-xs text-muted-foreground">
                  Avg {item.avgYearsShort.toFixed(1)} yrs short
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

